/**
 * 実績ストーリー（ADDICT → WORKS）。scroll-section-storyline の移植。
 * セクションをピン留めし、スクロール進行度で：
 *   Phase A (0–0.5)：ADDICT（SVGマスクの切り抜き穴）が scale 1→8 に拡大、終盤でフェードアウト
 *   Phase B (0.5–1)：中央の WORKS/実績紹介 がフェードイン
 *   全区間：背面の斜め写真グリッドが列ごとに無限スクロール
 * 進行度は service-stack と同じ「ページスクロール＋ピン」方式で算出。
 * 参照: addict_intraction / src/demos/scroll-section-storyline.astro
 * 対象: [data-story]（.story）
 */

import type { PageModule } from "./page-module"

const clamp = (v: number, min: number, max: number): number => Math.max(min, Math.min(max, v))
const lerp = (a: number, b: number, t: number): number => a + (b - a) * t
const easeOut = (t: number): number => 1 - Math.pow(1 - t, 3)

/** 列ごとの流れる向き・速さ（PC4列 / SP3列 とも先頭から使用） */
const COL_DIR = [-1, 1, -1, 1]
const COL_SPEED = [0.8, 0.95, 0.7, 0.85]

/**
 * WORKS完成(progress=1)後、背面グリッドだけ余分に進める「進行度の幅」。
 * この区間で速度を 1→0 へ徐々に落として止める（急停止しない）。
 * 実際に増える進行度は放物線のため GRID_OVERRUN/2（例:0.2→+0.1）。0=無効。
 */
const GRID_OVERRUN = 0.2

/**
 * ADDICT左スライドの焦点移動。「画面中央へピン留めする語の位置(cx)」を FROM→TO へ動かし、
 * zoomTが SLIDE_LOCK_AT に達したら TO で固定（ロック）＝以降はその点(D右側)を中央にピン留めしたまま深くズーム。
 * ★調整ポイント★
 *   SLIDE_TO      … 固定したい語の位置。語の中央=834 / 3文字目D右≒970 / C付近≒1079。
 *   SLIDE_LOCK_AT … 何割ズームした時点で固定するか(0〜1)。小さいほど早くロック＝ドリフトが減る(D固定が長い)。
 */
const SLIDE_FROM = 834
const SLIDE_TO = 973
const SLIDE_LOCK_AT = 0.7 // zoomT基準。0.6 = スクロール進行度0.3でロック（progress = zoomT×0.5）

/**
 * ADDICTズームの最大倍率を画面比率で自動調整：maxScale = ZOOM_K / fitScale（[12,50]でクランプ）。
 * 幅フィットのため横長画面ほど深ズームで縦がクロップされ「帯」になる → 横長ほど浅くズームして縦の見えを揃える。
 * 目安: SP(fitScale≒0.293)→50 / PC(≒1.015)→約14。ZOOM_Kを上げると全体的に深くズーム。
 */
const ZOOM_K = 50

/** ADDICTのズーム＆フェードアウトが完了する progress。この後 WORKS_START までは実写真だけの「タメ」。 */
const ADDICT_ZOOM_END = 0.5

/**
 * WORKS＋白veil のフェードイン開始 progress（大きいほど遅れて登場・完成は常に1.0）。
 * ADDICT_ZOOM_END〜この値の間はADDICTズーム完了後のタメ（背面写真だけ見える）になる。
 */
const WORKS_START = 0.68

/** SP（≤1024px）は3列、PCは4列 */
const desiredCols = (): number => (window.innerWidth <= 1024 ? 3 : 4)

/** SP判定（軽量化分岐用。desiredColsと同じ境界） */
const isSP = (): boolean => window.innerWidth <= 1024

/** SP時はグリッド画像を軽量なSP用avif(_sp)へ差し替え（デコードメモリを削減） */
const gridSrc = (url: string): string => (isSP() ? url.replace(/\.avif$/, "_sp.avif") : url)

/**
 * 初期化。reduced-motion では ADDICT を消して WORKS を静的表示。
 * @param {HTMLElement} scope 現在ページの <main>（要素走査の起点）
 * @returns {(() => void) | void} 後始末関数（対象要素が無ければ void）
 */
export const mountStory: PageModule = (scope) => {
  const section = scope.querySelector<HTMLElement>("[data-story]")
  if (!section) return
  const sticky = section.querySelector<HTMLElement>(".story__sticky")
  const addict = section.querySelector<HTMLElement>("[data-story-addict]")
  const addictSvg = section.querySelector<HTMLElement>(".story__addictSvg")
  // scaleはSVG要素ではなくマスク参照元のパス群(g)に効かせる（SVG要素scaleだとmaskがラスタライズ拡大でジャギる）
  const addictLetters = section.querySelector<SVGGElement>("#storyAddictLetters")
  const center = section.querySelector<HTMLElement>("[data-story-center]")
  const veil = section.querySelector<HTMLElement>(".story__veil")
  const grid = section.querySelector<HTMLElement>(".story__grid")
  if (!sticky || !addict || !addictSvg || !center || !grid) return

  // SSR(4列)から36枚のURLを順番通りに収集（各列の前半=1セット分）
  const storyUrls: string[] = []
  section.querySelectorAll<HTMLElement>("[data-story-col]").forEach((col) => {
    const imgs = Array.from(col.querySelectorAll<HTMLImageElement>(".story__img img"))
    const half = Math.floor(imgs.length / 2) || imgs.length
    for (let i = 0; i < half; i++) {
      const src = imgs[i].getAttribute("src")
      if (src) storyUrls.push(src)
    }
  })

  /**
   * URLリストを cols 列に分配して .story__grid を再構築（各列2セットで無限ループ）
   * @param {number} colCount 列数（PC4/SP3）
   */
  const buildGrid = (colCount: number): void => {
    if (storyUrls.length === 0) return
    const perCol = Math.ceil(storyUrls.length / colCount)
    grid.replaceChildren()
    for (let c = 0; c < colCount; c++) {
      const chunk = storyUrls.slice(c * perCol, (c + 1) * perCol)
      const colEl = document.createElement("div")
      colEl.className = "story__col"
      colEl.setAttribute("data-story-col", String(c))
      // 各列2セット出力（無限ループ用に half でラップ）。DOM生成で組む（innerHTML不使用）
      for (let rep = 0; rep < 2; rep++) {
        for (const url of chunk) {
          const wrap = document.createElement("div")
          wrap.className = "story__img"
          const pic = document.createElement("picture")
          const img = document.createElement("img")
          img.src = gridSrc(url)
          img.alt = ""
          img.loading = "lazy"
          img.decoding = "async"
          pic.appendChild(img)
          wrap.appendChild(pic)
          colEl.appendChild(wrap)
        }
      }
      grid.appendChild(colEl)
    }
  }

  let cols = Array.from(section.querySelectorAll<HTMLElement>("[data-story-col]"))
  let currentCols = cols.length

  // 高さ系（スクロール予算・各列の高さ）はスクロール中に変化しないので毎フレーム測らずキャッシュ。
  // aspect-ratio＋列幅がCSSで確定しているため画像デコード前でも正しい値が取れる（同期リフロー削減）。
  let scrollBudget = 1
  let colHalves: number[] = []
  const measure = (): void => {
    scrollBudget = Math.max(section.offsetHeight - sticky.clientHeight, 1)
    colHalves = cols.map((col) => col.scrollHeight / 2) // 2セット出力なので半分でループ
  }

  // 現在の幅に必要な列数へ組み直す（初回・リサイズで列数が変わった時のみ）
  const ensureCols = (): void => {
    const d = desiredCols()
    if (d === currentCols) return
    buildGrid(d)
    currentCols = d
    cols = Array.from(section.querySelectorAll<HTMLElement>("[data-story-col]"))
    measure() // 列数が変わったら高さを測り直す
  }

  ensureCols()

  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
    addict.style.opacity = "0"
    center.style.opacity = "1"
    if (veil) veil.style.opacity = "1" // WORKSと同じく白フロストも常時表示
    center.style.pointerEvents = "auto" // WORKS常時表示なのでBtnをクリック可能に
    return
  }

  // ADDICT(パス・幅1668)を可視横幅いっぱいにフィットさせる基準scale。
  // viewBox(1728×1080)+slice のため「見える横幅」がビューポート比で変わる
  // （横長=横フィット→viewBox全幅 / 縦長=縦フィット→中央クロップ）ので動的算出。
  let addictFitScale = 1
  const fitAddict = (): void => {
    const w = sticky.clientWidth
    const h = sticky.clientHeight
    if (!w || !h) return
    const sliceScale = Math.max(w / 1728, h / 1080)
    const visibleW = w / sliceScale // viewBox座標での可視横幅
    addictFitScale = (visibleW * 0.98) / 1668 // 可視横幅の98%に収める
    // 拡大は g(パス群)のベクターscaleで駆動するので、SVG要素側のCSS transformは使わない（シャープ維持・PC/SP共通）
    addictSvg.style.transform = ""
  }

  fitAddict()
  measure()

  let rafId: number | null = null
  let io: IntersectionObserver | null = null
  let inView = true // ピン区間が画面内にあるときだけ更新（他セクションのスクロールで無駄に走らせない）

  const update = (): void => {
    if (!inView) return
    const rect = section.getBoundingClientRect()
    const raw = -rect.top / scrollBudget // clampしない生の進行度（グリッドのオーバーラン用）
    const progress = clamp(raw, 0, 1)

    let addictScale: number
    let addictOpacity: number
    // Phase A(0–ADDICT_ZOOM_END)の進行度(0→1)。ズームも左スライドもこれに等速(linear)で連動（同時に開始＝ズレない）。
    const zoomT = clamp(progress / ADDICT_ZOOM_END, 0, 1)
    // 画面比率でズーム深さを自動調整（横長ほど浅く＝縦の帯化を防ぐ）。fitScaleはfitAddictで算出済み。
    const maxScale = clamp(ZOOM_K / addictFitScale, 12, 50)
    if (progress < ADDICT_ZOOM_END) {
      // Phase A：ADDICT 等速拡大（終盤 zoomT0.8–1.0＝progress0.4–0.5 でフェードアウト）
      addictScale = lerp(1, maxScale, zoomT)
      addictOpacity = 1 - clamp((zoomT - 0.8) / 0.2, 0, 1)
    } else {
      // Phase B以降：ADDICTは消えて最大ズームで固定
      addictScale = maxScale
      addictOpacity = 0
    }
    // WORKS＋白veil：WORKS_START(>ADDICT_ZOOM_END)から遅れて1.0までフェードイン。
    // ADDICT_ZOOM_END〜WORKS_START は実写真だけの「タメ」（centerOpacity=0）。
    const centerOpacity = easeOut(clamp((progress - WORKS_START) / (1 - WORKS_START), 0, 1))
    // PC/SP共通：参照元パス群(g)をベクターscale（maskが画面解像度で再描画されシャープ）。
    // 序盤(zoomT 0→SLIDE_LOCK_AT)で不変点をADDICT中央(834)→D右(SLIDE_TO)へ寄せ、以降はSLIDE_TOで固定＝
    // 深いズーム中はD右側を画面中央にピタッとピン留めしたまま拡大（ドリフト/overshootを防ぐ）。
    if (addictLetters) {
      // 左スライドは easeOut（勢いよく始動→D右手前で減速して着地）。SLIDE_LOCK_ATで到達→以降ロック(固定)。
      const slideT = easeOut(clamp(zoomT / SLIDE_LOCK_AT, 0, 1))
      const cx = lerp(SLIDE_FROM, SLIDE_TO, slideT)
      addictLetters.setAttribute("transform", `translate(864 540) scale(${addictFitScale * addictScale}) translate(${-cx} -170)`)
    }
    // ADDICTのフェードアウトはPC/SP共通（progress0.4→1のまま／0.4–0.5で1→0）。
    // SP塗り版は不透明グレーで背面を隠したままズーム→ズーム完了の0.4–0.5でカーテンが上がり実績写真＋WORKSをリビール。
    addict.style.opacity = String(addictOpacity)
    center.style.opacity = String(centerOpacity)
    // 白フロスト(.story__veil)はWORKSと同じ透明度カーブで駆動（同じ値を共有＝ズレない）
    if (veil) veil.style.opacity = String(centerOpacity)
    // WORKSが見えているPhase Bのときだけクリック可能に（Phase Aの透明Btn誤クリック防止）
    center.style.pointerEvents = centerOpacity > 0.5 ? "auto" : "none"

    // 背面グリッド：列ごとに無限スクロール（高さはキャッシュ値を使用）。
    // WORKS完成(progress=1)後のオーバーラン域は、速度を 1→0 へ徐々に落として止める（急停止しない）。
    // 増分 = over - over²/(2·OVERRUN)＝放物線（開始傾き1でPhaseと連続 → 終端で傾き0＝なめらか停止）。
    const over = clamp(raw - 1, 0, GRID_OVERRUN)
    const gridProgress = clamp(raw, 0, 1) + over - (over * over) / (2 * GRID_OVERRUN)
    const rawY = gridProgress * 1500
    cols.forEach((col, i) => {
      const half = colHalves[i]
      if (!half) return
      const raw = (COL_DIR[i] ?? -1) * rawY * (COL_SPEED[i] ?? 0.8)
      const wrapped = ((raw % half) + half) % half
      col.style.translate = `0 ${-wrapped}px`
    })
  }

  const onScroll = (): void => {
    if (rafId !== null) return
    rafId = requestAnimationFrame(() => {
      rafId = null
      update()
    })
  }

  // 画面内にある時だけ計算する（ピン区間は長いので、それ以外でのgetBoundingClientRect churnを避ける）
  if ("IntersectionObserver" in window) {
    io = new IntersectionObserver(
      (entries) => {
        inView = entries[0].isIntersecting
        if (inView) update()
      },
      { rootMargin: "0px" }
    )
    io.observe(section)
  }

  // リサイズ：列数の組み直し→フィット→高さ再計測→再描画をまとめて実行
  const onResize = (): void => {
    ensureCols()
    fitAddict()
    measure()
    update()
  }

  const ac = new AbortController()
  window.addEventListener("scroll", onScroll, { passive: true, signal: ac.signal })
  window.addEventListener("resize", onResize, { passive: true, signal: ac.signal })
  // 画像・フォント確定後にもう一度だけ高さを測り直す（保険）。
  // swup 遷移では load が再発火しないため、フォント確定を毎ページ document.fonts.ready で待つ。
  document.fonts?.ready.then(() => {
    measure()
    update()
  })
  update()

  return () => {
    ac.abort()
    io?.disconnect()
    if (rafId !== null) cancelAnimationFrame(rafId)
  }
}
